import React, { useEffect } from "react"
import { useSearchParams } from "react-router-dom"

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs"
import { cn } from "@pma/utils/helpers"
import { MainBoQDetails } from "./main-boq-items"
import { ItemRequestDetails } from "./item-request-details"
import { BillDetails } from "./bill-details"
import { Card, CardContent } from "@components/common/card"
import { DictType } from "@pma/types"
import { useAuth } from "@pma/stores/auth-store"
import useTranslator from "@pma/hooks/use-translator"

interface Props {
  boq: DictType
}

const MainBoqTabs = ({ boq }: Props) => {
  const { translate, locale } = useTranslator()
  const [searchParams] = useSearchParams()

  const { authConfig, setTabManager } = useAuth()

  // Support both ?subtab=bills and ?tab=bills
  const subtabFromUrl = searchParams.get("subtab") || searchParams.get("tab")
  const defaultSubChild = subtabFromUrl === "bills" ? "bills" : "boq-items"

  const allStateTabs = authConfig?.tabs ?? {}
  const mainBoQActiveTab = allStateTabs ? allStateTabs["boq"] : undefined
  const activeTabs: DictType = mainBoQActiveTab || {
    main: "main",
    sub: "boq-tender",
    subChild: defaultSubChild,
  }

  // Force store to match URL param — overrides stale store value from previous visit
  useEffect(() => {
    if (subtabFromUrl === "bills" && activeTabs?.subChild !== "bills") {
      setTabManager({
        group: "boq",
        main: "main",
        sub: "boq-tender",
        subChild: "bills",
      })
    }
  }, [subtabFromUrl])

  const isHebrew = locale === "he"

  const boqId = boq?.id

  return (
    <Card
      className='w-full space-y-6 overflow-x-hidden border-none shadow-none md:max-w-xl lg:max-w-3xl xl:max-w-6xl'
      style={{ minWidth: "-webkit-fill-available" }}
    >
      <CardContent className='!p-0 pb-20 md:py-4'>
        <Tabs
          value={activeTabs?.subChild || defaultSubChild}
          className={cn("relative overflow-x-auto", isHebrew ? "rtl" : "ltr")}
        >
          <TabsList className='flex items-center justify-center bg-transparent'>
            <TabsTrigger
              value='boq-items'
              className={cn(
                "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                activeTabs?.subChild === "boq-items" && "border-b-2",
              )}
              onClick={() => {
                setTabManager({
                  group: "boq",
                  main: "main",
                  sub: "boq-tender",
                  subChild: "boq-items",
                })
              }}
            >
              {translate("BoQ Items")}
            </TabsTrigger>

            <TabsTrigger
              value='item-requests'
              className={cn(
                "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                activeTabs?.subChild === "item-requests" && "border-b-2",
              )}
              onClick={() => {
                setTabManager({
                  group: "boq",
                  main: "main",
                  sub: "boq-tender",
                  subChild: "item-requests",
                })
              }}
            >
              {translate("Item Requests")}
            </TabsTrigger>

            <TabsTrigger
              value='bills'
              className={cn(
                "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                activeTabs?.subChild === "bills" && "border-b-2",
              )}
              onClick={() => {
                setTabManager({
                  group: "boq",
                  main: "main",
                  sub: "boq-tender",
                  subChild: "bills",
                })
              }}
            >
              {translate("Bills")}
            </TabsTrigger>
          </TabsList>

          <TabsContent forceMount value={activeTabs?.subChild || defaultSubChild} className={cn("p-0")}>
            {(!activeTabs?.subChild || activeTabs?.subChild === "boq-items") && (
              <MainBoQDetails boqId={boqId} />
            )}

            {activeTabs?.subChild === "item-requests" && <ItemRequestDetails boqId={boqId} />}

            {activeTabs?.subChild === "bills" && (
              <BillDetails boqId={String(boq?.id)} projectId={String(boq?.project_id)} />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}

export default MainBoqTabs
